import { useState, Suspense, lazy, useEffect, Fragment } from "react";
import { tss } from "tss-react/mui";
import {
    Button,
    InputAdornment,
    OutlinedInput,
    CircularProgress,
    Tabs,
    Tab,
    MenuItem,
    Typography,
    Input,
    Tooltip,
    Divider,
} from "@mui/material";
import { Scrollbars } from "react-custom-scrollbars";
import { icons } from "constants/viewer";
import {
    getFileIcon,
    typeInFilter,
    filterInputTypes,
    getWorkflowsVersions,
    findSelectedObjects,
} from "actions/GlobalActions";
import { handleLayers, handleClick, addObjectsWithInputs } from "actions/ViewerTools";
import ApiUtils from "api/Utils";
import ObjectInfo from "./ObjectInfo";
import ObjectVersion from "./ObjectVersion";
import { ModalWindow, CustomRichSelect } from "components";
import EventsEmitter from "events/EventsEmitter";
import { FoldersActions } from "actions";
import { Search, Error } from "@mui/icons-material";
import commonStyles, { panelHeaderColor } from "styles/common";
import { alpha } from '@mui/material/styles';
import { useJumboTheme } from 'themes/jumbo/@jumbo/components/JumboTheme/hooks';

const MAX_HEIGHT = 510;
const MAX_SCROLLER_HEIGHT = MAX_HEIGHT - 10;
const MENU_LIST_WIDTH = 600;

var useStyles = tss.create(({theme}) => ({
    root: {
        position: "absolute",
        display: "table",
        //backgroundColor: panelHeaderColor,
        background: alpha(theme.palette.background.default, 0.85),
        width: "100%",
        transition: "height 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms, width 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms",
        userSelect: "none",
        color: theme.palette.text.primary,
    },
    button: {
        minWidth: 20,
        padding: "5px 5px",
        marginLeft: 5
    },
    addSelected: {
        position: "absolute",
        right: 20,
        bottom: 15,
        width: 146,
    },
    closeButton: {
        display: "inline-block",
        position: "absolute",
        top: 3,
        right: 9
    },
    addObjectTitle: {
        fontSize: 20,
        position: "relative",
        left: 11,
    },
    table: {
        width: "100%",
        height: "100%",
    },
    cellTop: {
        verticalAlign: "top",
        height: 46,
    },
    content: {
        overflowY: "auto",
        height: MAX_HEIGHT,
        //backgroundColor: "white",
    },
    firstColumn: {
        width: 180,
        height: "100%",
        verticalAlign: "top",
        textAlign: "center",
        borderRight: `solid 1px  ${theme.palette.divider}`,
        pointerEvents: "auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        overflowX: "hidden",
        //backgroundColor: "#FFFFFF",
    },
    secondColumn: {
        verticalAlign: "top",
        pointerEvents: "auto",
        width: "100%",
        backgroundColor: theme.palette.background.paper
    },
    selectedObject: {
        fontWeight: 600,
        padding: 7,
    },
    selectedElements: {
        paddingBottom: 15,
        textAlign: "left",
        fontSize: 12,
    },
    selectedElementsCounter: {
        borderBottom: `solid 1px  ${theme.palette.divider}`,
        textAlign: "center",
        paddingBottom: 5,
    },
    fullWidth: {
        width: 220,
        height: 40,
        marginBottom: 5,
        maxWidth: 200,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    icon: {
        width: 24,
        height: 24,
        color: theme.palette.text.secondary,//"#9c9c9c",
    },
    layerIcon: {
        display: "inline-flex",
        alignItems: "center",
        minWidth: 24,
        maxHeight: 24,
    },
    row: {
        height: 35,
        display: "flex",
        alignItems: "center",
        width: "100%",
        position: "relative",
    },
    header: {
        textTransform: "capitalize",
        color: theme.palette.text.primary,//"#9c9c9c",
        fontSize: 11,
        lineHeight: 1.66666667,
        whiteSpace: "nowrap",
    },
    search: {
        height: 27,
        width: 240,
        borderRadius: 16,
        marginRight: 10,
    },
    bottomRight: {
        align: "right",
    },
    list: {
        paddingLeft: 36,
    },
}));

let AddObjectsList: any,
    selectedBox: any,
    inputValues: any = {},
    layer: any = {},
    cachedInputs: any = {};

function getIcon(classes: any, file: any) {
    const fileName = getFileIcon(file);
    //console.log("fileName:", fileName);
    const Icon = icons[fileName];
    if (Icon) {
        return (
            <div className={classes.layerIcon}>
                <Icon className={classes.icon} />
            </div>
        );
    }
}

const ObjectsTree = (props: { allObjects: any; setEntity: any; selectedEntities: any }) => {
    const { classes } = useStyles();
    const { allObjects, setEntity, selectedEntities } = props;

    const renderedObjects = findSelectedObjects(allObjects, selectedEntities);
    //console.log("renderedObjects:", renderedObjects);

    return (
        <Fragment>
            {renderedObjects.map((object: any, index: number) => {
                const workflow = FoldersActions.getWorkflowFromEntity(object) || [];
                const workflow_id = FoldersActions.getEntityWorkflow(object) || "unversioned";
                if (workflow && workflow["version"]) {
                    return (
                        <ObjectInfo
                            key={"program-head-" + index}
                            index={index}
                            object={object}
                            setEntity={setEntity}
                            hasChildren={true}
                        >
                            <div className={classes.list}>
                                {allObjects[workflow_id][object["name"]].map((child: any, index: number) => (
                                    <ObjectVersion
                                        key={"program-" + index}
                                        index={index}
                                        object={child}
                                        setEntity={setEntity}
                                        selected={object["ID"] === child["ID"]}
                                    />
                                ))}
                            </div>
                        </ObjectInfo>
                    );
                }
                return <ObjectInfo key={"program-head-" + index} index={index} object={object} setEntity={setEntity} />;
            })}
        </Fragment>
    );
};

const EntitiesSelect = (props: {
    currentProject: any;
    inputs: any;
    input: any;
    entity: any;
    setSelected: any;
    onChange: any;
}) => {
    const { classes } = useStyles();
    const { currentProject, inputs, input, entity, setSelected } = props;
    const requiredValues: any = inputs.filter((input: any) => input["type"] === "3");

    const [value, setValue] = useState(null);
    const [selectedEntities, setSelectedEntities] = useState({});

    useEffect(() => {
        setValue(null);
        setSelected([]);
        inputValues = {};
        //setInputValues({});
    }, [input, setSelected]);

    var objects: any = [],
        allObjects: any = {};

    const setEntity = (index: any, entry: any) => {
        const curInputValues: any = { ...inputValues };
        curInputValues["" + index] = entry;

        layer["name"] = entity["name"];
        layer["view_type"] = entity["program"] ? entity["program"] : entity["ID"];
        layer["input_value_" + input["id"]] = entry["ID"];
        layer["project"] = entity["project"];

        if (requiredValues.length === Object.keys(curInputValues).length) {
            setSelected(layer);
        }
        setValue(entry);
        EventsEmitter.emit("closeMenu", true);
        inputValues = curInputValues;
        const se: any = { ...selectedEntities };
        const workflow_id = FoldersActions.getEntityWorkflow(entry) || "unversioned";
        se[workflow_id] = entry;
        setSelectedEntities(se);
    };

    const handleNone = () => {
        setValue(null);
        setSelected([]);
        EventsEmitter.emit("closeMenu", true);
    };

    if (entity && entity["file_type"]) {
        objects = Object.values(currentProject).filter((entry: any) => typeInFilter(entry, input));
    }

    allObjects = getWorkflowsVersions(objects);

    //console.log("allObjects:", allObjects);

    return (
        <CustomRichSelect
            menuListProps={{ style: { width: MENU_LIST_WIDTH } }}
            content={value === null ? null : <ObjectInfo index={0} object={value} setEntity={setEntity} />}
            className={classes.fullWidth}
            menuMaxHeight={400}
            maxWidth={350}
            style={{ width: 350, maxWidth: 350 }}
        >
            <MenuItem value="None" onClick={handleNone}>
                None
            </MenuItem>
            <ObjectsTree allObjects={allObjects} setEntity={setEntity} selectedEntities={selectedEntities} />
        </CustomRichSelect>
    );
};

const InputSection = (props: { currentProject: any; inputs: any; input: any; entity: any; setSelected: any }) => {
    const { classes, cx } = useStyles();
    const { classes: common } = commonStyles();
    const { input } = props;
    const inputType = parseInt(input["type"]);
    let curValue = "";
    switch (inputType) {
        case 0:
            curValue = input["default"] ? input["default"] : 0;
            break;
        case 2:
        case 3:
            curValue = "";
            break;
        default:
            break;
    }
    const label = input["label"];
    const [value, setValue] = useState(curValue);

    const onChange = (evt: any) => {
        setValue(evt.target.value);
        layer["input_value_" + input["id"]] = evt.target.value;
    };

    return (
        <div style={{ padding: 10 }}>
            <div className={classes.row}>
                <Typography className={cx(common.middle, common.subSectionLabel)}>{label}</Typography>
            </div>
            <div className={classes.row}>
                {inputType === 0 ? (
                    <Input
                        type="number"
                        inputProps={{ min: 0, step: 0.1, max: 1 }}
                        value={value}
                        className={classes.fullWidth}
                        onChange={onChange}
                    />
                ) : inputType === 2 ? (
                    <Input value={value} className={classes.fullWidth} onChangeCapture={onChange} />
                ) : (
                    <EntitiesSelect onChange={onChange} {...props} />
                )}
            </div>
        </div>
    );
};

const fetchInputs = (entityId: string, callback: any) => {
    if (cachedInputs[entityId]) {
        const response = { result: { status: "success" }, inputs: cachedInputs[entityId] };
        callback(response);
    } else {
        ApiUtils.fetchProgramInputs(entityId, (response: any) => {
            callback(response);
        });
    }
};

const ObjectTypes = (props: { currentProject: any; setSelected: any; setInputs: any }) => {
    const { classes, cx } = useStyles();
    const { classes: common } = commonStyles();
    const { currentProject } = props;
    const [selected, setSelected] = useState("");
    const [inputs, setInputs] = useState([]);
    const [error, setError] = useState("");
    const [entity, setEntity] = useState(null);

    const defaultPrograms = Object.values(currentProject).filter(
        (entry: any) => entry["virtual"] === "1" && entry["ID"].includes("com.voxelfarm.program")
    );

    const customPrograms = Object.values(currentProject).filter(
        (entry: any) =>
            ((entry["program_type"] && entry["program_type"] === "VIEW") || entry["file_type"] === "VOXGEN") &&
            !defaultPrograms.map((entry: any) => entry["ID"]).includes(entry["ID"])
    );

    const selectProgram = (key: string, entry: any) => {
        let programId: string = entry["ID"];
        if (key.includes("custom")) {
            if (typeof entry["program"] !== "undefined") programId = entry["program"];
        }

        fetchInputs(programId, (response: any) => {
            if (response["result"]["status"] === "success") {
                let curInputs: any = response["inputs"];
                cachedInputs[programId] = curInputs;
                let filteredInputs = curInputs.filter(filterInputTypes);
                setInputs(filteredInputs);
                const globalInputs: any = {};
                globalInputs[programId] = curInputs;
                props.setInputs(globalInputs);
                setEntity(entry);
                setError("");
            } else {
                const errorMessage = atob(response["result"]["message"]);
                setError(errorMessage);
            }
        });
        setSelected(key);
    };

    return (
        <table className={classes.table}>
            <tbody>
                <tr>
                    <td className={classes.firstColumn} style={{ width: 230 }}>
                        <Scrollbars
                            autoHeight
                            autoHeightMax={MAX_SCROLLER_HEIGHT}
                            style={{ marginTop: 10, overflowX: "hidden" }}
                        >
                            <Fragment>
                                {defaultPrograms.map((entry: any, index: number) => (
                                    <Tooltip key={"program-" + index} title={entry["name"]}>
                                        <MenuItem
                                            className={cx(
                                                classes.fullWidth,
                                                common.smallLabel,
                                                selected === "program-" + index ? common.selected : ""
                                            )}
                                            value={entry["name"]}
                                            onClick={() => {
                                                selectProgram("program-" + index, entry);
                                            }}
                                        >
                                            {getIcon(classes, entry)}
                                            <Typography style={{ paddingLeft: 4 }}>{entry["name"]}</Typography>
                                        </MenuItem>
                                    </Tooltip>
                                ))}
                                <Divider />
                                {customPrograms.map((entry: any, index: number) => (
                                    <Tooltip key={"custom-" + index} title={entry["name"]}>
                                        <MenuItem
                                            className={cx(
                                                classes.fullWidth,
                                                common.smallLabel,
                                                selected === "custom-" + index ? common.selected : ""
                                            )}
                                            value={entry["name"]}
                                            onClick={() => {
                                                selectProgram("custom-" + index, entry);
                                            }}
                                        >
                                            {getIcon(classes, entry)}
                                            <Typography style={{ paddingLeft: 4 }}>{entry["name"]}</Typography>
                                        </MenuItem>
                                    </Tooltip>
                                ))}
                            </Fragment>
                        </Scrollbars>
                    </td>
                    <td className={classes.secondColumn}>
                        <div className={classes.content} style={{ padding: 10 }}>
                            <Scrollbars autoHeightMax={MAX_SCROLLER_HEIGHT} style={{ overflowX: "hidden" }}>
                                {inputs.length === 0 && error.length === 0 ? (
                                    <Typography>Please select a Program from the left</Typography>
                                ) : error.length > 0 ? (
                                    <div className={classes.layerIcon}>
                                        <Error className={classes.icon} style={{ color: "red" }} />
                                        <Typography>{error}</Typography>
                                    </div>
                                ) : (
                                    inputs.map((input: any, index: number) => (
                                        <InputSection
                                            key={"input-" + index}
                                            inputs={inputs}
                                            input={input}
                                            entity={entity}
                                            {...props}
                                        />
                                    ))
                                )}
                            </Scrollbars>
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>
    );
};

const DataSets = (props: { filter: any; selected: any; setSelected: any }) => {
    const { classes } = useStyles();
    const { classes: common } = commonStyles();
    const { selected } = props;

    const [show, setShow] = useState(false);

    useEffect(() => {
        AddObjectsList = lazy(() => import("./AddObjectsList"));
        setTimeout(() => setShow(true), 200);
        return () => {
            AddObjectsList = null;
            setShow(false);
        };
    }, []);
    return (
        <table className={classes.table}>
            <tbody>
                <tr>
                    <td className={classes.firstColumn}>
                        <Scrollbars autoHeight autoHeightMax={MAX_SCROLLER_HEIGHT} style={{ overflowX: "hidden" }}>
                            <div>
                                <div className={classes.selectedObject}>Selected Objects</div>
                                <div className={classes.selectedElements} ref={(elm: any) => (selectedBox = elm)}>
                                    <div className={classes.selectedElementsCounter}>{selected.length} selected</div>
                                </div>
                            </div>
                        </Scrollbars>
                    </td>
                    <td className={classes.secondColumn}>
                        <div className={classes.content}>
                            <Scrollbars autoHeightMax={MAX_SCROLLER_HEIGHT} style={{ overflowX: "hidden" }}>
                                <Suspense
                                    fallback={
                                        <div className={common.middleCenter} style={{ width: "100%", height: "100%" }}>
                                            Loading...
                                        </div>
                                    }
                                >
                                    {show && AddObjectsList && AddObjectsList !== null ? (
                                        <AddObjectsList {...props} selectedBox={selectedBox} />
                                    ) : (
                                        <div className={common.middleCenter} style={{ width: "100%", height: "100%" }}>
                                            <CircularProgress />
                                        </div>
                                    )}
                                </Suspense>
                            </Scrollbars>
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>
    );
};

type IProps = {
    open: boolean;
    view: any;
    currentProject: any;
    source?: string;
    handleClose: () => void;
};

export default function AddObjectsPanel(props: IProps) {
    const { classes } = useStyles();
    const { classes: common } = commonStyles();
    const { currentProject, source, open, handleClose } = props;
    const [tab, setTab] = useState(0);
    const [selected, setSelected] = useState<any>([]);
    const [disabled, setDisabled] = useState(false);
    const [inputs, setInputs] = useState({});
    const [filter, setFilter] = useState("");
    const { theme } = useJumboTheme();

    const handleChange = (event: any) => {
        setFilter(event.target.value.toLowerCase());
    };

    const addSelectedToView = (evt: any) => {
        evt.preventDefault();
        setDisabled(true);
        if (typeof selected === "object" && !Array.isArray(selected)) {
            addObjectsWithInputs(selected, inputs, (object: any) => {
                if (object !== null) {
                    handleLayers({ ...object });
                }
                setSelected([]);
                handleClose();
            });
        } else {
            //console.log("selected:", selected);
            var entities = selected.map((elem: any) => {
                return elem.id ? elem.id.replace("chk-", "") : elem["ID"];
            });
            console.log("ids:", entities);
            if (entities.length > 0) {
                handleClick(
                    {
                        cmd: "addLayers",
                        param: {
                            currentProject: currentProject,
                            entities: entities,
                            source: typeof source === "string" ? source : "0",
                        },
                    },
                    (object: any) => {
                        if (object !== null) {
                            handleLayers({ ...object });
                        }
                        setSelected([]);
                        handleClose();
                    }
                );
            }
        }
    };

    return (
        <div id="addObjectsPanel" className={classes.root}>
            <ModalWindow
                width={1000}
                height={680}
                open={open}
                handleClose={handleClose}
                paperStyle={{ background: alpha(theme.palette.background.default, 0.85), color: theme.palette.text.primary, paddingLeft: 0, paddingRight: 0 }}
            >
                <table className={classes.table}>
                    <tbody>
                        <tr>
                            <td colSpan={2} className={classes.cellTop}>
                                <div className={common.middle} style={{ position: "relative", width: "100%" }}>
                                    <div className={classes.addObjectTitle}>Add Object to View</div>
                                    <div
                                        style={{
                                            width: "calc(100% - 200px)",
                                            display: "inline-flex",
                                            left: 30,
                                            alignItems: "center",
                                            justifyContent: "flex-end",
                                        }}
                                    >
                                        <OutlinedInput
                                            className={classes.search}
                                            placeholder="Search..."
                                            startAdornment={
                                                <InputAdornment position="start">
                                                    <Search />
                                                </InputAdornment>
                                            }
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <Fragment>
                    <Tabs
                        value={tab}
                        onChange={(e: any, newValue: number) => setTab(newValue)}
                        indicatorColor="primary"
                        textColor="primary"
                        className={common.tabs}
                        style={{ width: "100%" }}
                    >
                        <Tab value={0} label="DATASETS" className={common.tab} />
                        <Tab value={1} label="OBJECT TYPES" className={common.tab} />
                    </Tabs>
                    <div style={{ height: MAX_HEIGHT }}>
                        {tab === 0 ? (
                            <DataSets filter={filter} selected={selected} setSelected={setSelected} {...props} />
                        ) : (
                            <ObjectTypes setSelected={setSelected} setInputs={setInputs} {...props} />
                        )}
                    </div>
                </Fragment>
                <div className={classes.bottomRight}>
                    <Button
                        onClick={addSelectedToView}
                        aria-label="Add Selected"
                        title="Add Selected"
                        className={[classes.button, classes.addSelected].join(" ")}
                        variant="contained"
                        color="primary"
                        disabled={disabled || selected.length === 0 ? true : false}
                    >
                        Add Selected
                    </Button>
                </div>
            </ModalWindow>
        </div>
    );
}
